import { createFileRoute, redirect } from '@tanstack/react-router'
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useAuthStore } from '~/client/common/stores/auth'
import SlideEditor from '~/client/components/slide-editor/components/SlideEditor'
import { useEditorStore } from '~/client/components/slide-editor/store'
import {
  listTemplateDefinitions,
  getTemplateDefinition,
} from '~/client/components/present/common/templateRegistry'
import type {
  PresentationTemplateDefinition,
  SlideTemplateDefinition,
  TemplateElementDef,
  TemplateTextElementDef,
  TemplateImageElementDef,
  TemplateShapeElementDef,
} from '~/client/components/present/common/templateDefinition'
import type {
  Slide,
  SlideElement,
  TextElement,
  ImageElement,
  ShapeElement,
  TextStyle,
  BorderStyle,
} from '~/client/components/slide-editor/types'
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  DEFAULT_TEXT_STYLE,
  DEFAULT_BORDER,
  DEFAULT_PADDING,
  DEFAULT_CONSTRAINTS,
  computeInsets,
} from '~/client/components/slide-editor/types'

export const Route = createFileRoute('/admin/template-editor')({
  beforeLoad: () => {
    if (typeof window === 'undefined') return
    const { userDetail } = useAuthStore.getState()
    if (userDetail && !userDetail.is_superuser) {
      throw redirect({
        to: '/admin/recs',
        search: { eventId: undefined, tab: undefined, status: undefined, city: undefined, sort: undefined },
      })
    }
  },
  component: AdminTemplateEditorPage,
})

// ─── Helpers: Convert TemplateElementDef[] → Slide (same as presentationGenerator) ──

let _idCounter = 0
function genId(prefix = 'el'): string {
  _idCounter += 1
  return `${prefix}_${Date.now()}_${_idCounter}_${Math.random().toString(36).slice(2, 6)}`
}

function mergeTextStyle(partial: Partial<TextStyle>): TextStyle {
  return { ...DEFAULT_TEXT_STYLE, ...partial }
}

function mergeBorder(partial?: Partial<BorderStyle>): BorderStyle {
  return { ...DEFAULT_BORDER, ...(partial || {}) }
}

function mergePadding(
  partial?: { top: number; right: number; bottom: number; left: number },
): { top: number; right: number; bottom: number; left: number } {
  return { ...DEFAULT_PADDING, ...(partial || {}) }
}

function templateDefToSlide(
  template: SlideTemplateDefinition,
  slideName: string,
): Slide {
  const elements: SlideElement[] = []
  let zIndex = 1
  const parentSize = { width: CANVAS_WIDTH, height: CANVAS_HEIGHT }

  for (const def of template.elements) {
    let element: SlideElement

    switch (def.type) {
      case 'text': {
        const textDef = def as TemplateTextElementDef
        element = {
          id: genId('text'),
          type: 'text' as const,
          name: textDef.name,
          rect: { ...textDef.rect },
          rotation: textDef.rotation ?? 0,
          opacity: textDef.opacity ?? 1,
          locked: textDef.locked ?? false,
          visible: textDef.visible ?? true,
          zIndex: textDef.zIndex ?? zIndex,
          constraints: { ...DEFAULT_CONSTRAINTS },
          constraintInsets: computeInsets(textDef.rect, parentSize),
          groupId: null,
          content: textDef.content, // Keep raw template string with {{variables}}
          textStyle: mergeTextStyle(textDef.textStyle),
          backgroundColor: textDef.backgroundColor ?? 'transparent',
          padding: mergePadding(textDef.padding),
          border: mergeBorder(textDef.border),
        } as TextElement
        break
      }
      case 'image': {
        const imgDef = def as TemplateImageElementDef
        element = {
          id: genId('img'),
          type: 'image' as const,
          name: imgDef.name,
          rect: { ...imgDef.rect },
          rotation: imgDef.rotation ?? 0,
          opacity: imgDef.opacity ?? 1,
          locked: imgDef.locked ?? false,
          visible: imgDef.visible ?? true,
          zIndex: imgDef.zIndex ?? zIndex,
          constraints: { ...DEFAULT_CONSTRAINTS },
          constraintInsets: computeInsets(imgDef.rect, parentSize),
          groupId: null,
          src: imgDef.src,
          alt: imgDef.alt ?? '',
          objectFit: imgDef.objectFit ?? 'fill',
          border: mergeBorder(imgDef.border),
          backgroundColor: imgDef.backgroundColor ?? 'transparent',
          cropRegion: null,
        } as ImageElement
        break
      }
      case 'shape': {
        const shapeDef = def as TemplateShapeElementDef
        element = {
          id: genId('shape'),
          type: 'shape' as const,
          name: shapeDef.name,
          rect: { ...shapeDef.rect },
          rotation: shapeDef.rotation ?? 0,
          opacity: shapeDef.opacity ?? 1,
          locked: shapeDef.locked ?? false,
          visible: shapeDef.visible ?? true,
          zIndex: shapeDef.zIndex ?? zIndex,
          constraints: { ...DEFAULT_CONSTRAINTS },
          constraintInsets: computeInsets(shapeDef.rect, parentSize),
          groupId: null,
          shapeKind: shapeDef.shapeKind,
          fill: shapeDef.fill ?? 'transparent',
          border: mergeBorder(shapeDef.border),
          shadow: null,
        } as ShapeElement
        break
      }
      default:
        zIndex += 1
        continue
    }

    elements.push(element)
    zIndex += 1
  }

  return {
    id: genId('slide'),
    name: slideName,
    backgroundColor: template.backgroundColor,
    width: template.width ?? CANVAS_WIDTH,
    height: template.height ?? CANVAS_HEIGHT,
    elements,
  }
}

// ─── Helpers: Convert Slide → TemplateElementDef[] (reverse) ────────────────

function slideElementToTemplateDef(
  el: SlideElement,
  originalDefs: TemplateElementDef[],
): TemplateElementDef | null {
  // Try to find the matching original def by name to preserve conditionalVariable
  const originalDef = originalDefs.find((d) => d.name === el.name)

  const rect = { x: el.rect.x, y: el.rect.y, width: el.rect.width, height: el.rect.height }

  switch (el.type) {
    case 'text':
    case 'markdown': {
      const textEl = el as TextElement
      const textDef: TemplateTextElementDef = {
        type: 'text',
        name: textEl.name,
        content: textEl.content,
        rect,
        zIndex: textEl.zIndex,
        textStyle: buildPartialTextStyle(textEl.textStyle),
        backgroundColor: textEl.backgroundColor,
        padding: { ...textEl.padding },
      }
      if (textEl.rotation !== 0) textDef.rotation = textEl.rotation
      if (textEl.opacity !== 1) textDef.opacity = textEl.opacity
      if (textEl.visible === false) textDef.visible = false
      if (textEl.locked) textDef.locked = true
      if (textEl.border && textEl.border.width > 0) {
        textDef.border = { width: textEl.border.width, color: textEl.border.color, radius: textEl.border.radius }
      }
      // Preserve conditionalVariable from original
      if (originalDef && 'conditionalVariable' in originalDef && originalDef.conditionalVariable) {
        textDef.conditionalVariable = originalDef.conditionalVariable
      }
      return textDef
    }
    case 'image': {
      const imgEl = el as ImageElement
      const imgDef: TemplateImageElementDef = {
        type: 'image',
        name: imgEl.name,
        src: imgEl.src,
        rect,
        zIndex: imgEl.zIndex,
        objectFit: imgEl.objectFit,
        backgroundColor: imgEl.backgroundColor,
      }
      if (imgEl.alt) imgDef.alt = imgEl.alt
      if (imgEl.rotation !== 0) imgDef.rotation = imgEl.rotation
      if (imgEl.opacity !== 1) imgDef.opacity = imgEl.opacity
      if (imgEl.visible === false) imgDef.visible = false
      if (imgEl.locked) imgDef.locked = true
      if (imgEl.border && imgEl.border.width > 0) {
        imgDef.border = { width: imgEl.border.width, color: imgEl.border.color, radius: imgEl.border.radius }
      }
      // Preserve conditionalVariable from original
      if (originalDef && 'conditionalVariable' in originalDef && originalDef.conditionalVariable) {
        imgDef.conditionalVariable = originalDef.conditionalVariable
      }
      return imgDef
    }
    case 'shape': {
      const shapeEl = el as ShapeElement
      const shapeDef: TemplateShapeElementDef = {
        type: 'shape',
        name: shapeEl.name,
        shapeKind: shapeEl.shapeKind as 'rectangle' | 'ellipse' | 'line',
        rect,
        fill: shapeEl.fill,
        zIndex: shapeEl.zIndex,
      }
      if (shapeEl.rotation !== 0) shapeDef.rotation = shapeEl.rotation
      if (shapeEl.opacity !== 1) shapeDef.opacity = shapeEl.opacity
      if (shapeEl.visible === false) shapeDef.visible = false
      if (shapeEl.locked) shapeDef.locked = true
      if (shapeEl.border && shapeEl.border.width > 0) {
        shapeDef.border = { width: shapeEl.border.width, color: shapeEl.border.color, radius: shapeEl.border.radius }
      }
      // Preserve conditionalVariable from original
      if (originalDef && 'conditionalVariable' in originalDef && originalDef.conditionalVariable) {
        shapeDef.conditionalVariable = originalDef.conditionalVariable
      }
      return shapeDef
    }
    default:
      return null
  }
}

function buildPartialTextStyle(style: TextStyle): Partial<TextStyle> {
  const partial: Partial<TextStyle> = {}
  if (style.fontFamily !== DEFAULT_TEXT_STYLE.fontFamily) partial.fontFamily = style.fontFamily
  if (style.fontSize !== DEFAULT_TEXT_STYLE.fontSize) partial.fontSize = style.fontSize
  if (style.fontWeight !== DEFAULT_TEXT_STYLE.fontWeight) partial.fontWeight = style.fontWeight
  if (style.fontStyle !== DEFAULT_TEXT_STYLE.fontStyle) partial.fontStyle = style.fontStyle
  if (style.lineHeight !== DEFAULT_TEXT_STYLE.lineHeight) partial.lineHeight = style.lineHeight
  if (style.color !== DEFAULT_TEXT_STYLE.color) partial.color = style.color
  if (style.textAlign !== DEFAULT_TEXT_STYLE.textAlign) partial.textAlign = style.textAlign
  if (style.verticalAlign !== DEFAULT_TEXT_STYLE.verticalAlign) partial.verticalAlign = style.verticalAlign
  if (style.textDecoration !== DEFAULT_TEXT_STYLE.textDecoration) partial.textDecoration = style.textDecoration
  if (style.letterSpacing !== DEFAULT_TEXT_STYLE.letterSpacing) partial.letterSpacing = style.letterSpacing
  if (style.textTransform !== DEFAULT_TEXT_STYLE.textTransform) partial.textTransform = style.textTransform

  // Always include essential properties to match existing templateDef format
  partial.fontFamily = style.fontFamily
  partial.fontSize = style.fontSize
  partial.fontWeight = style.fontWeight
  partial.fontStyle = style.fontStyle
  partial.lineHeight = style.lineHeight
  partial.color = style.color
  partial.textAlign = style.textAlign
  partial.verticalAlign = style.verticalAlign
  if (style.textTransform && style.textTransform !== 'none') {
    partial.textTransform = style.textTransform
  }

  return partial
}

// ─── Code generation ────────────────────────────────────────────────────────

function generateTemplateCode(
  templateDef: PresentationTemplateDefinition,
  coverSlide: Slide,
  spaceSlide: Slide,
): string {
  const originalCoverDefs = templateDef.cover.elements
  const originalSpaceDefs = templateDef.space.elements

  const coverElements = coverSlide.elements
    .map((el) => slideElementToTemplateDef(el, originalCoverDefs))
    .filter(Boolean) as TemplateElementDef[]

  const spaceElements = spaceSlide.elements
    .map((el) => slideElementToTemplateDef(el, originalSpaceDefs))
    .filter(Boolean) as TemplateElementDef[]

  const lines: string[] = []

  lines.push(`import type {`)
  lines.push(`  PresentationTemplateDefinition,`)
  lines.push(`  SlideTemplateDefinition,`)
  lines.push(`  TemplateElementDef,`)
  lines.push(`} from '../templateDefinition'`)
  lines.push(``)

  // Detect static asset references in the original template to generate import placeholders
  if (templateDef.staticAssets) {
    lines.push(`// TODO: Update these imports to match your asset paths`)
    for (const [key, value] of Object.entries(templateDef.staticAssets)) {
      const safeName = key.replace(/[^a-zA-Z0-9]/g, '_')
      lines.push(`// import ${safeName} from '${value}'`)
    }
    lines.push(``)
  }

  lines.push(`const zeroPadding = { top: 0, right: 0, bottom: 0, left: 0 }`)
  lines.push(``)

  // Cover elements
  lines.push(`const coverElements: TemplateElementDef[] = ${stringifyElementDefs(coverElements)}`)
  lines.push(``)

  // Space elements
  lines.push(`const spaceElements: TemplateElementDef[] = ${stringifyElementDefs(spaceElements)}`)
  lines.push(``)

  // Cover template
  lines.push(`const coverTemplate: SlideTemplateDefinition = {`)
  lines.push(`  id: '${templateDef.cover.id}',`)
  lines.push(`  name: '${templateDef.cover.name}',`)
  lines.push(`  kind: '${templateDef.cover.kind}',`)
  lines.push(`  backgroundColor: '${coverSlide.backgroundColor}',`)
  lines.push(`  width: ${coverSlide.width},`)
  lines.push(`  height: ${coverSlide.height},`)
  lines.push(`  elements: coverElements,`)
  lines.push(`}`)
  lines.push(``)

  // Space template
  lines.push(`const spaceTemplate: SlideTemplateDefinition = {`)
  lines.push(`  id: '${templateDef.space.id}',`)
  lines.push(`  name: '${templateDef.space.name}',`)
  lines.push(`  kind: '${templateDef.space.kind}',`)
  lines.push(`  backgroundColor: '${spaceSlide.backgroundColor}',`)
  lines.push(`  width: ${spaceSlide.width},`)
  lines.push(`  height: ${spaceSlide.height},`)
  lines.push(`  elements: spaceElements,`)
  lines.push(`}`)
  lines.push(``)

  // Main export
  const exportName = `${templateDef.id.replace(/[^a-zA-Z0-9]/g, '')}Template`
  lines.push(`export const ${exportName}: PresentationTemplateDefinition = {`)
  lines.push(`  id: '${templateDef.id}',`)
  lines.push(`  name: '${templateDef.name}',`)
  lines.push(`  cover: coverTemplate,`)
  lines.push(`  space: spaceTemplate,`)
  if (templateDef.googleFontsUrl) {
    lines.push(`  googleFontsUrl:`)
    lines.push(`    '${templateDef.googleFontsUrl}',`)
  }
  if (templateDef.staticAssets) {
    lines.push(`  staticAssets: {`)
    for (const [key, value] of Object.entries(templateDef.staticAssets)) {
      lines.push(`    ${key}: '${value}',`)
    }
    lines.push(`  },`)
  }
  lines.push(`}`)
  lines.push(``)

  return lines.join('\n')
}

function stringifyElementDefs(defs: TemplateElementDef[]): string {
  // Pretty-print the array with proper indentation
  const items = defs.map((def) => {
    const lines: string[] = []
    lines.push(`  {`)

    // Type
    lines.push(`    type: '${def.type}',`)
    lines.push(`    name: '${escapeString(def.name)}',`)

    // Type-specific properties
    switch (def.type) {
      case 'shape': {
        const shape = def as TemplateShapeElementDef
        lines.push(`    shapeKind: '${shape.shapeKind}',`)
        lines.push(`    rect: { x: ${shape.rect.x}, y: ${shape.rect.y}, width: ${shape.rect.width}, height: ${shape.rect.height} },`)
        if (shape.fill) lines.push(`    fill: '${shape.fill}',`)
        lines.push(`    zIndex: ${shape.zIndex ?? 0},`)
        if (shape.locked) lines.push(`    locked: true,`)
        if (shape.rotation) lines.push(`    rotation: ${shape.rotation},`)
        if (shape.opacity !== undefined && shape.opacity !== 1) lines.push(`    opacity: ${shape.opacity},`)
        if (shape.visible === false) lines.push(`    visible: false,`)
        if (shape.border) {
          lines.push(`    border: { width: ${shape.border.width}, color: '${shape.border.color}'${shape.border.radius !== undefined ? `, radius: ${shape.border.radius}` : ''} },`)
        }
        if (shape.conditionalVariable) lines.push(`    conditionalVariable: '${shape.conditionalVariable}',`)
        break
      }
      case 'text': {
        const text = def as TemplateTextElementDef
        lines.push(`    content: '${escapeString(text.content)}',`)
        lines.push(`    rect: { x: ${text.rect.x}, y: ${text.rect.y}, width: ${text.rect.width}, height: ${text.rect.height} },`)
        lines.push(`    zIndex: ${text.zIndex ?? 0},`)
        if (text.conditionalVariable) lines.push(`    conditionalVariable: '${text.conditionalVariable}',`)
        if (text.locked) lines.push(`    locked: true,`)
        if (text.rotation) lines.push(`    rotation: ${text.rotation},`)
        if (text.opacity !== undefined && text.opacity !== 1) lines.push(`    opacity: ${text.opacity},`)
        if (text.visible === false) lines.push(`    visible: false,`)
        // textStyle
        if (text.textStyle && Object.keys(text.textStyle).length > 0) {
          lines.push(`    textStyle: {`)
          const ts = text.textStyle
          if (ts.fontFamily) lines.push(`      fontFamily: '${ts.fontFamily}',`)
          if (ts.fontSize !== undefined) lines.push(`      fontSize: ${ts.fontSize},`)
          if (ts.fontWeight !== undefined) lines.push(`      fontWeight: '${ts.fontWeight}',`)
          if (ts.fontStyle) lines.push(`      fontStyle: '${ts.fontStyle}',`)
          if (ts.lineHeight !== undefined) lines.push(`      lineHeight: ${ts.lineHeight},`)
          if (ts.color) lines.push(`      color: '${ts.color}',`)
          if (ts.textAlign) lines.push(`      textAlign: '${ts.textAlign}',`)
          if (ts.verticalAlign) lines.push(`      verticalAlign: '${ts.verticalAlign}',`)
          if (ts.textTransform && ts.textTransform !== 'none') lines.push(`      textTransform: '${ts.textTransform}',`)
          if (ts.textDecoration && ts.textDecoration !== 'none') lines.push(`      textDecoration: '${ts.textDecoration}',`)
          if (ts.letterSpacing !== undefined && ts.letterSpacing !== 0) lines.push(`      letterSpacing: ${ts.letterSpacing},`)
          lines.push(`    },`)
        }
        if (text.backgroundColor) lines.push(`    backgroundColor: '${text.backgroundColor}',`)
        if (text.padding) {
          const p = text.padding
          if (p.top === 0 && p.right === 0 && p.bottom === 0 && p.left === 0) {
            lines.push(`    padding: zeroPadding,`)
          } else {
            lines.push(`    padding: { top: ${p.top}, right: ${p.right}, bottom: ${p.bottom}, left: ${p.left} },`)
          }
        }
        if (text.border) {
          lines.push(`    border: { width: ${text.border.width}, color: '${text.border.color}'${text.border.radius !== undefined ? `, radius: ${text.border.radius}` : ''} },`)
        }
        break
      }
      case 'image': {
        const img = def as TemplateImageElementDef
        lines.push(`    src: '${escapeString(img.src)}',`)
        if (img.alt) lines.push(`    alt: '${escapeString(img.alt)}',`)
        lines.push(`    rect: { x: ${img.rect.x}, y: ${img.rect.y}, width: ${img.rect.width}, height: ${img.rect.height} },`)
        lines.push(`    zIndex: ${img.zIndex ?? 0},`)
        if (img.objectFit) lines.push(`    objectFit: '${img.objectFit}',`)
        if (img.backgroundColor) lines.push(`    backgroundColor: '${img.backgroundColor}',`)
        if (img.locked) lines.push(`    locked: true,`)
        if (img.rotation) lines.push(`    rotation: ${img.rotation},`)
        if (img.opacity !== undefined && img.opacity !== 1) lines.push(`    opacity: ${img.opacity},`)
        if (img.visible === false) lines.push(`    visible: false,`)
        if (img.border) {
          lines.push(`    border: { width: ${img.border.width}, color: '${img.border.color}'${img.border.radius !== undefined ? `, radius: ${img.border.radius}` : ''} },`)
        }
        if (img.conditionalVariable) lines.push(`    conditionalVariable: '${img.conditionalVariable}',`)
        break
      }
    }

    lines.push(`  },`)
    return lines.join('\n')
  })

  return `[\n${items.join('\n')}\n]`
}

function escapeString(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
}

// ─── Component ──────────────────────────────────────────────────────────────

function AdminTemplateEditorPage() {
  const templates = listTemplateDefinitions()
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [loadedTemplate, setLoadedTemplate] = useState<PresentationTemplateDefinition | null>(null)
  const [exportCode, setExportCode] = useState<string | null>(null)
  const [activeSlideKind, setActiveSlideKind] = useState<'cover' | 'space'>('cover')
  const codeRef = useRef<HTMLTextAreaElement>(null)
  const [copyFeedback, setCopyFeedback] = useState(false)

  const loadSlides = useEditorStore((s) => s.loadSlides)
  const setPresentationTitle = useEditorStore((s) => s.setPresentationTitle)
  const slides = useEditorStore((s) => s.slides)
  const activeSlideIndex = useEditorStore((s) => s.activeSlideIndex)
  const setActiveSlideIndex = useEditorStore((s) => s.setActiveSlideIndex)

  // Load template into the editor
  const handleLoadTemplate = useCallback(
    (templateId: string) => {
      const template = getTemplateDefinition(templateId)
      if (!template) return

      setSelectedTemplateId(templateId)
      setLoadedTemplate(template)
      setExportCode(null)

      const coverSlide = templateDefToSlide(template.cover, `Cover Slide (${template.name})`)
      const spaceSlide = templateDefToSlide(template.space, `Space Slide (${template.name})`)

      loadSlides([coverSlide, spaceSlide])
      setPresentationTitle(`Template: ${template.name}`)
      setActiveSlideKind('cover')
    },
    [loadSlides, setPresentationTitle],
  )

  // Track which slide kind is active
  useEffect(() => {
    if (activeSlideIndex === 0) {
      setActiveSlideKind('cover')
    } else if (activeSlideIndex === 1) {
      setActiveSlideKind('space')
    }
  }, [activeSlideIndex])

  // Export the current slides as TypeScript templateDef code
  const handleExport = useCallback(() => {
    if (!loadedTemplate || slides.length < 2) return

    const coverSlide = slides[0]
    const spaceSlide = slides[1]

    const code = generateTemplateCode(loadedTemplate, coverSlide, spaceSlide)
    setExportCode(code)
  }, [loadedTemplate, slides])

  // Copy code to clipboard
  const handleCopy = useCallback(() => {
    if (!exportCode) return
    navigator.clipboard.writeText(exportCode).then(() => {
      setCopyFeedback(true)
      setTimeout(() => setCopyFeedback(false), 2000)
    })
  }, [exportCode])

  // Download code as .ts file
  const handleDownload = useCallback(() => {
    if (!exportCode || !loadedTemplate) return
    const blob = new Blob([exportCode], { type: 'text/typescript' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `templateDef.ts`
    a.click()
    URL.revokeObjectURL(url)
  }, [exportCode, loadedTemplate])

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0 gap-4">
        <div className="flex items-center gap-3">
          {/* Admin badge */}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wide">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
            </svg>
            Admin
          </span>

          <span className="text-sm font-semibold text-gray-700">Template Editor</span>

          {/* Template selector */}
          <select
            value={selectedTemplateId ?? ''}
            onChange={(e) => {
              if (e.target.value) handleLoadTemplate(e.target.value)
            }}
            className="h-8 px-3 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#8A3DFF]/30 focus:border-[#8A3DFF]"
          >
            <option value="">Select template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.id})
              </option>
            ))}
          </select>

          {/* Slide kind indicator */}
          {loadedTemplate && (
            <div className="flex items-center gap-1 ml-2">
              <button
                type="button"
                onClick={() => setActiveSlideIndex(0)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  activeSlideKind === 'cover'
                    ? 'bg-[#8A3DFF] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Cover
              </button>
              <button
                type="button"
                onClick={() => setActiveSlideIndex(1)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  activeSlideKind === 'space'
                    ? 'bg-[#8A3DFF] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Space
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Export button */}
          {loadedTemplate && (
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 h-8 px-4 text-sm font-medium text-white bg-[#8A3DFF] rounded-md hover:bg-[#7B2FEE] transition-colors focus:outline-none focus:ring-2 focus:ring-[#8A3DFF]/30"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <polyline points="16 13 12 17 8 13" />
                <line x1="12" y1="17" x2="12" y2="9" />
              </svg>
              Export templateDef.ts
            </button>
          )}
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────────────── */}
      {!loadedTemplate ? (
        <div className="flex-1 flex items-center justify-center bg-[#fafafa]">
          <div className="flex flex-col items-center gap-4 text-center max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8A3DFF]/20 to-[#8A3DFF]/5 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8A3DFF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Template Definition Editor</h2>
              <p className="text-sm text-gray-500">
                Select a presentation template above to load its cover and space slide definitions into the slide editor.
                After making changes, click <strong>Export templateDef.ts</strong> to generate the updated TypeScript code.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Slide editor */}
          <div className={`flex-1 min-h-0 ${exportCode ? 'w-[60%]' : 'w-full'}`}>
            <SlideEditor />
          </div>

          {/* Export panel */}
          {exportCode && (
            <div className="w-[40%] min-w-[360px] max-w-[640px] border-l border-gray-200 bg-[#1e1e2e] flex flex-col shrink-0">
              {/* Export panel header */}
              <div className="h-10 bg-[#2a2a3d] border-b border-[#3a3a50] flex items-center justify-between px-3 shrink-0">
                <span className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
                  Generated Code — templateDef.ts
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-300 bg-[#3a3a50] rounded hover:bg-[#4a4a60] transition-colors"
                  >
                    {copyFeedback ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-300 bg-[#3a3a50] rounded hover:bg-[#4a4a60] transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportCode(null)}
                    className="inline-flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-200 transition-colors"
                    title="Close export panel"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Code display */}
              <textarea
                ref={codeRef}
                value={exportCode}
                readOnly
                className="flex-1 w-full p-4 bg-[#1e1e2e] text-[#cdd6f4] text-xs font-mono leading-relaxed resize-none focus:outline-none selection:bg-[#45475a]"
                spellCheck={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
